import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import {
    LayoutDashboard, Users, KanbanSquare, Sparkles, CalendarHeart,
    Mail, LineChart, ListChecks, Workflow, Bell, Search, Sun, Moon,
    LogOut, Command, Wand2, FileSpreadsheet, Database,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { TID } from "@/constants/testIds";
import { CommandPalette } from "@/components/layout/CommandPalette";
import { AIChatDrawer } from "@/components/ai/AIChatDrawer";
import { Toaster } from "@/components/ui/sonner";

const NAV = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, tid: TID.navDashboard, end: true },
    { to: "/crm", label: "CRM", icon: Users, tid: TID.navCrm },
    { to: "/pipeline", label: "Sales Pipeline", icon: KanbanSquare, tid: TID.navPipeline },
    { to: "/marketing", label: "Marketing", icon: Sparkles, tid: TID.navMarketing },
    { to: "/health-calendar", label: "Health Calendar", icon: CalendarHeart, tid: TID.navHealthCalendar },
    { to: "/newsletter", label: "Newsletter", icon: Mail, tid: TID.navNewsletter },
    { to: "/analytics", label: "Analytics", icon: LineChart, tid: TID.navAnalytics },
    { to: "/tasks", label: "Tasks", icon: ListChecks, tid: TID.navTasks },
    { to: "/automations", label: "Automations", icon: Workflow, tid: TID.navAutomations },
    { to: "/actions", label: "AI Action Center", icon: Wand2, tid: TID.navActions },
    { to: "/alerts", label: "Alerts", icon: Bell, tid: TID.navAlerts },
];

const ADMIN_NAV = [
    { to: "/contacts", label: "Contatti (Excel)", icon: FileSpreadsheet, tid: TID.navContacts },
    { to: "/admin/import", label: "Import Excel", icon: Database, tid: TID.navImport },
];

export default function AppLayout() {
    const { user, logout } = useAuth();
    const { theme, toggle } = useTheme();
    const [paletteOpen, setPaletteOpen] = useState(false);
    const [aiOpen, setAiOpen] = useState(false);
    const loc = useLocation();

    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setPaletteOpen((o) => !o);
            }
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "j") {
                e.preventDefault();
                setAiOpen((o) => !o);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    return (
        <div className="min-h-screen">
            {/* Sidebar */}
            <aside
                className="fixed inset-y-0 left-0 w-64 border-r border-border/[var(--border-opacity)] pv-glass z-30 flex flex-col"
                data-testid="app-sidebar"
            >
                <div className="px-6 pt-7 pb-6">
                    <div className="flex items-center gap-2.5">
                        <div className="h-8 w-8 rounded-xl bg-primary/10 flex items-center justify-center relative overflow-hidden">
                            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-transparent to-azure/20" />
                            <div className="h-3 w-3 rounded-full bg-primary relative pv-pulse-ring" />
                        </div>
                        <div className="flex flex-col leading-tight">
                            <span className="text-[15px] font-medium tracking-tight">Prime Vision</span>
                            <span className="text-[10px] text-textsubtle uppercase tracking-[0.14em]">Command Center</span>
                        </div>
                    </div>
                </div>

                <div className="px-3">
                    <button
                        data-testid={TID.searchOpen}
                        onClick={() => setPaletteOpen(true)}
                        className="w-full flex items-center justify-between gap-2 rounded-xl border border-border/[var(--border-opacity)] bg-surface/40 hover:bg-surface transition-colors px-3 py-2 text-sm text-textmuted"
                    >
                        <span className="flex items-center gap-2">
                            <Search size={15} strokeWidth={1.5} />
                            Cerca ovunque
                        </span>
                        <kbd className="pv-mono text-[10px] px-1.5 py-0.5 rounded-md border border-border/[var(--border-opacity)] text-textsubtle">⌘K</kbd>
                    </button>
                </div>

                <nav className="mt-5 px-3 flex-1 overflow-y-auto pv-scroll">
                    <div className="pv-eyebrow px-3 mb-2">Workspace</div>
                    <ul className="space-y-0.5">
                        {NAV.map((n) => (
                            <li key={n.to}>
                                <NavLink
                                    to={n.to}
                                    end={n.end}
                                    data-testid={n.tid}
                                    className={({ isActive }) =>
                                        `flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13.5px] transition-colors ${
                                            isActive
                                                ? "bg-primary/[0.08] text-primary font-medium"
                                                : "text-textmain/80 hover:bg-surface2"
                                        }`
                                    }
                                >
                                    <n.icon size={15.5} strokeWidth={1.5} />
                                    {n.label}
                                </NavLink>
                            </li>
                        ))}
                    </ul>
                    <div className="pv-eyebrow px-3 mt-5 mb-2">Dati reali</div>
                    <ul className="space-y-0.5">
                        {ADMIN_NAV.map((n) => (
                            <li key={n.to}>
                                <NavLink
                                    to={n.to}
                                    data-testid={n.tid}
                                    className={({ isActive }) =>
                                        `flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13.5px] transition-colors ${
                                            isActive
                                                ? "bg-primary/[0.08] text-primary font-medium"
                                                : "text-textmain/80 hover:bg-surface2"
                                        }`
                                    }
                                >
                                    <n.icon size={15.5} strokeWidth={1.5} />
                                    {n.label}
                                </NavLink>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className="border-t border-border/[var(--border-opacity)] px-3 py-3 flex items-center gap-2">
                    <div className="flex items-center gap-2 flex-1 px-2">
                        <div className="h-7 w-7 rounded-full bg-primary text-primary-foreground text-[11px] font-medium flex items-center justify-center">
                            {(user?.name || "P")[0]}
                        </div>
                        <div className="flex flex-col leading-tight min-w-0">
                            <span className="text-[12px] font-medium truncate">{user?.name || "Utente"}</span>
                            <span className="text-[10px] text-textsubtle truncate">{user?.email}</span>
                        </div>
                    </div>
                    <button
                        onClick={toggle}
                        data-testid={TID.themeToggle}
                        className="h-7 w-7 rounded-lg hover:bg-surface2 flex items-center justify-center text-textmuted transition-colors"
                        aria-label="Cambia tema"
                    >
                        {theme === "dark" ? <Sun size={14.5} strokeWidth={1.5} /> : <Moon size={14.5} strokeWidth={1.5} />}
                    </button>
                    <button
                        onClick={logout}
                        data-testid={TID.logout}
                        className="h-7 w-7 rounded-lg hover:bg-surface2 flex items-center justify-center text-textmuted transition-colors"
                        aria-label="Esci"
                    >
                        <LogOut size={14.5} strokeWidth={1.5} />
                    </button>
                </div>
            </aside>

            {/* Content */}
            <main className="ml-64 min-h-screen bg-background">
                <div className="sticky top-0 z-20 pv-glass border-b border-border/[var(--border-opacity)]">
                    <div className="flex items-center justify-between px-8 py-3">
                        <div className="text-[12px] text-textsubtle pv-mono">
                            {loc.pathname === "/" ? "/dashboard" : loc.pathname}
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setPaletteOpen(true)}
                                className="hidden md:flex items-center gap-1.5 text-[12px] text-textmuted hover:text-textmain px-2.5 py-1 rounded-lg hover:bg-surface2 transition-colors"
                            >
                                <Command size={13} strokeWidth={1.5} /> K
                            </button>
                            <button
                                onClick={() => setAiOpen(true)}
                                data-testid={TID.aiChatOpen}
                                className="group flex items-center gap-2 text-[13px] font-medium px-3 py-1.5 rounded-xl bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
                            >
                                <Sparkles size={14} strokeWidth={1.5} className="group-hover:rotate-12 transition-transform" />
                                Prime Vision AI
                            </button>
                        </div>
                    </div>
                </div>

                <div className="px-8 pt-8 pb-16 max-w-[1600px]">
                    <Outlet />
                </div>
            </main>

            <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
            <AIChatDrawer open={aiOpen} onClose={() => setAiOpen(false)} />
            <Toaster />
        </div>
    );
}
